// src/config/gameRules.js

export const ABILITY_SCORES = {
    Strength: {
      name: "Strength",
      abbreviation: "STR",
      measures: "Natural athleticism, bodily power",
      importantFor: ["Barbarian", "Fighter", "Paladin"],
      description: "Strength measures bodily power, athletic training, and the extent to which you can exert raw physical force.",
      racialIncreases: {
        "Mountain Dwarf": 2,
        "Dragonborn": 2,
        "Half-Orc": 2,
        "Human": 1
      }
    },
    Dexterity: {
      name: "Dexterity",
      abbreviation: "DEX",
      measures: "Physical agility, reflexes, balance, poise",
      importantFor: ["Monk", "Ranger", "Rogue"],
      description: "Dexterity measures agility, reflexes, and balance.",
      racialIncreases: {
        "Elf": 2,
        "Halfling": 2,
        "Forest Gnome": 1,
        "Human": 1
      }
    },
    Constitution: {
      name: "Constitution",
      abbreviation: "CON",
      measures: "Health, stamina, vital force",
      importantFor: ["All Classes"],
      description: "Constitution measures health, stamina, and vital force.",
      racialIncreases: {
        "Dwarf": 2,
        "Stout Halfling": 1,
        "Rock Gnome": 1,
        "Half-Orc": 1,
        "Human": 1
      }
    },
    Intelligence: {
      name: "Intelligence",
      abbreviation: "INT",
      measures: "Mental acuity, information recall, analytical skill",
      importantFor: ["Wizard"],
      description: "Intelligence measures mental acuity, accuracy of recall, and the ability to reason.",
      racialIncreases: {
        "High Elf": 1,
        "Gnome": 2,
        "Tiefling": 1,
        "Human": 1
      }
    },
    Wisdom: {
      name: "Wisdom",
      abbreviation: "WIS",
      measures: "Awareness, intuition, insight",
      importantFor: ["Cleric", "Druid"],
      description: "Wisdom reflects how attuned you are to the world around you and represents perceptiveness and intuition.",
      racialIncreases: {
        "Hill Dwarf": 1,
        "Wood Elf": 1,
        "Human": 1
      }
    },
    Charisma: {
      name: "Charisma",
      abbreviation: "CHA",
      measures: "Confidence, eloquence, leadership",
      importantFor: ["Bard", "Sorcerer", "Warlock"],
      description: "Charisma measures your ability to interact effectively with others.",
      racialIncreases: {
        "Half-Elf": 2,
        "Drow": 1,
        "Lightfoot Halfling": 1,
        "Dragonborn": 1,
        "Tiefling": 2,
        "Human": 1
      }
    }
  };
  
  export const CLASSES = [
    {
      name: "Fighter",
      primaryAbility: "Strength",
      suggestedAbilities: ["Strength", "Constitution"],
      hitDie: 10,
      description: "Masters of martial combat, skilled with a variety of weapons and armor."
    },
    {
      name: "Wizard",
      primaryAbility: "Intelligence",
      suggestedAbilities: ["Intelligence", "Constitution"],
      hitDie: 6,
      description: "Scholarly magic-users capable of manipulating the structures of reality."
    },
    {
      name: "Rogue",
      primaryAbility: "Dexterity",
      suggestedAbilities: ["Dexterity", "Intelligence"],
      hitDie: 8,
      description: "Skilled tricksters who specialize in stealth and precision strikes."
    },
    {
      name: "Cleric",
      primaryAbility: "Wisdom",
      suggestedAbilities: ["Wisdom", "Constitution"],
      hitDie: 8,
      description: "Divine spellcasters who wield the power of their deity."
    },
    {
      name: "Paladin",
      primaryAbility: "Strength",
      suggestedAbilities: ["Strength", "Charisma"],
      hitDie: 10,
      description: "Holy warriors bound to sacred oaths."
    },
    {
      name: "Ranger",
      primaryAbility: "Dexterity",
      suggestedAbilities: ["Dexterity", "Wisdom"],
      hitDie: 10,
      description: "Warriors who specialize in hunting and tracking."
    }
  ];
  
  export const RACES = [
    {
      name: "Human",
      description: "Versatile and ambitious, humans are the most common race.",
      abilityScoreIncrease: {
        all: 1
      },
      speed: 30,
      traits: ["Versatile", "Extra Language"]
    },
    {
      name: "Elf",
      description: "Graceful and long-lived, elves are magical people of otherworldly grace.",
      abilityScoreIncrease: {
        Dexterity: 2
      },
      speed: 30,
      traits: ["Darkvision", "Keen Senses", "Fey Ancestry", "Trance"]
    },
    {
      name: "Dwarf",
      description: "Bold and hardy, dwarves are known as skilled warriors, miners, and workers of stone and metal.",
      abilityScoreIncrease: {
        Constitution: 2
      },
      speed: 25,
      traits: ["Darkvision", "Dwarven Resilience", "Stonecunning"]
    }
  ];
  
  export const ABILITY_MODIFIERS = [
    { score: 1, modifier: -5 },
    { score: 2, modifier: -4 },
    { score: 3, modifier: -4 },
    { score: 4, modifier: -3 },
    { score: 5, modifier: -3 },
    { score: 6, modifier: -2 },
    { score: 7, modifier: -2 },
    { score: 8, modifier: -1 },
    { score: 9, modifier: -1 },
    { score: 10, modifier: 0 },
    { score: 11, modifier: 0 },
    { score: 12, modifier: 1 },
    { score: 13, modifier: 1 },
    { score: 14, modifier: 2 },
    { score: 15, modifier: 2 },
    { score: 16, modifier: 3 },
    { score: 17, modifier: 3 },
    { score: 18, modifier: 4 },
    { score: 19, modifier: 4 },
    { score: 20, modifier: 5 }
  ];
  
  export const ALIGNMENTS = [
    {
      name: "Lawful Good",
      description: "Believes in truth and justice, following the rules."
    },
    {
      name: "Neutral Good",
      description: "Does what's good without bias for or against order."
    },
    {
      name: "Chaotic Good",
      description: "Acts as their conscience directs, without regard for rules."
    },
    {
      name: "Lawful Neutral",
      description: "Acts according to law, tradition, or personal codes."
    },
    {
      name: "True Neutral",
      description: "Prefers to avoid taking sides in moral conflicts."
    },
    {
      name: "Chaotic Neutral",
      description: "Follows their whims, valuing their own liberty."
    },
    {
      name: "Lawful Evil",
      description: "Methodically takes what they want within their code."
    },
    {
      name: "Neutral Evil",
      description: "Does whatever they can get away with."
    },
    {
      name: "Chaotic Evil",
      description: "Acts with arbitrary violence and destruction."
    }
  ];
  
  // Helper functions
  export const getAbilityModifier = (score) => {
    const entry = ABILITY_MODIFIERS.find(mod => score <= mod.score);
    return entry ? entry.modifier : Math.floor((score - 10) / 2);
  };
  
  export const getRacialIncrease = (ability, race) => {
    return ABILITY_SCORES[ability]?.racialIncreases[race] || 0;
  };
  
  export const getBaseHitPoints = (characterClass) => {
    const classInfo = CLASSES.find(c => c.name === characterClass);
    return classInfo ? classInfo.hitDie : 8;
  };
  
  export const getSuggestedAbilities = (characterClass) => {
    const classInfo = CLASSES.find(c => c.name === characterClass);
    return classInfo ? classInfo.suggestedAbilities : [];
  };
  
  export const getRaceTraits = (race) => {
    const raceInfo = RACES.find(r => r.name === race);
    return raceInfo ? raceInfo.traits : [];
  };
  
  export const calculateTotalAbilityScore = (baseScore, ability, race) => {
    return baseScore + getRacialIncrease(ability, race);
  };
  
  export const isAbilityRecommendedForClass = (ability, characterClass) => {
    const suggestedAbilities = getSuggestedAbilities(characterClass);
    return suggestedAbilities.includes(ability);
  };